import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Alert, Badge, Button, Card, PageHeader, Spinner } from "@empite/ui-components";
import type { Creation, CreationFile } from "../marketplace-client";
import {
  useCounters,
  useCreation,
  useDownload,
  useSetLike,
  useSetPublished,
  useViewer,
} from "../marketplace-hooks";
import { formatBytes, startDownload } from "../upload";
import { Comments } from "./comments";
import { ReportForm } from "./report";
import {
  ErrorAlert,
  FilePlaceholder,
  StateBadge,
  moderationTone,
  statusTone,
  when,
} from "./common";

/**
 * One creation, as a reader sees it.
 *
 * The page renders whatever the API returned and asks it nothing twice: a
 * draft reaches this screen only because the signed-in mount let its author
 * read it back, and a stranger asking for the same id gets a 404 from the
 * server rather than a hidden section here.
 */
export function CreationDetail({ creationID }: { creationID: string }) {
  const viewer = useViewer();
  const creation = useCreation(creationID);
  const counters = useCounters(creationID);
  const like = useSetLike();
  const [reporting, setReporting] = useState(false);

  if (creation.isPending) {
    return <Spinner label="Loading this creation" />;
  }
  if (creation.error || !creation.data) {
    return (
      <ErrorAlert
        error={creation.error ?? new Error("That creation is not available.")}
        title="We could not open this creation"
      />
    );
  }

  const item = creation.data;
  const mine = viewer.userID !== null && viewer.userID === item.author_user_id;
  const liked = counters.data?.liked ?? false;

  return (
    <>
      <PageHeader
        title={item.title}
        description={item.summary}
        actions={mine ? <AuthorControls creation={item} /> : null}
      />

      <p className="esm-creation__meta">
        {item.author_display_name ? `by ${item.author_display_name} · ` : ""}
        <Badge tone={statusTone(item.status)}>{item.status}</Badge>{" "}
        <Badge tone={moderationTone(item.moderation_state)}>{item.moderation_state}</Badge>{" "}
        published {when(item.published_at)}
      </p>

      {item.status !== "published" ? (
        <Alert tone="warning" title="Not published">
          Only you can see this. Downloads are refused for everyone — including you — until it is
          published.
        </Alert>
      ) : null}

      <Card title="About">
        <p className="esm-creation__description">{item.description || "No description."}</p>
      </Card>

      <Card title="Files">
        <Files creation={item} />
      </Card>

      <Card title="Reactions">
        <ErrorAlert error={like.error} title="That did not register" />
        <p className="esm-creation__counts">
          <span>{counters.data?.like_count ?? item.like_count} likes</span>
          <span>{counters.data?.comment_count ?? item.comment_count} comments</span>
          <span>{counters.data?.download_count ?? item.download_count} downloads</span>
        </p>
        {viewer.signedIn ? (
          <div className="esm-actions">
            {/* The button sends the STATE it wants, so pressing it twice, or
                pressing it again after a lost response, converges on one like
                rather than toggling wherever the race left it. */}
            <Button
              variant={liked ? "secondary" : "primary"}
              busy={like.isPending}
              onClick={() => like.mutate({ creationID, liked: !liked })}
            >
              {liked ? "Liked" : "Like"}
            </Button>
            {!mine ? (
              <Button variant="ghost" onClick={() => setReporting((was) => !was)}>
                Report
              </Button>
            ) : null}
          </div>
        ) : null}
        {reporting ? (
          <ReportForm
            subject={{ creationID }}
            what="creation"
            onDone={() => setReporting(false)}
          />
        ) : null}
      </Card>

      <Comments creationID={creationID} isCreationAuthor={mine} />
    </>
  );
}

/**
 * The file list, with the generic placeholder Phase 1 mandates.
 *
 * Nothing here branches on the declared filename or content type: the portal
 * does not sniff, preview, thumbnail or unpack an uploaded binary, because
 * the platform never interprets one either (docs/architecture.md §18). The
 * size and checksum shown are the ones the STORE measured, not the ones the
 * uploader declared — an unverified file has neither.
 */
function Files({ creation }: { creation: Creation }) {
  const files = creation.files ?? [];
  if (files.length === 0) {
    return <p>No files yet.</p>;
  }
  return (
    <ul className="esm-file-list">
      {files.map((file) => (
        <li key={file.id}>
          <FileRow creationID={creation.id} file={file} published={creation.status === "published"} />
        </li>
      ))}
    </ul>
  );
}

function FileRow({
  creationID,
  file,
  published,
}: {
  creationID: string;
  file: CreationFile;
  published: boolean;
}) {
  const viewer = useViewer();
  const download = useDownload();

  return (
    <div className="esm-file">
      <FilePlaceholder />
      <div className="esm-file__body">
        <strong>{file.declared_filename}</strong>
        <p className="esm-file__meta">
          {formatBytes(file.byte_size ?? undefined)} · <StateBadge file={file} />
        </p>
        {file.checksum_sha256 ? (
          <p className="esm-file__checksum">
            <code>sha256:{file.checksum_sha256}</code>
          </p>
        ) : null}
      </div>
      <div className="esm-file__actions">
        <ErrorAlert error={download.error} title="That download was refused" />
        {viewer.signedIn ? (
          <Button
            busy={download.isPending}
            // Disabled only for what the server would refuse anyway. The
            // button is a shortcut past a 403, never the check itself: the
            // authorisation lives in AuthorizeDownload and is re-made on
            // every press.
            disabled={!published || file.state !== "active"}
            onClick={() =>
              download.mutate({ creationID, fileID: file.id }, { onSuccess: startDownload })
            }
          >
            Download
          </Button>
        ) : (
          <span className="esm-file__meta">Sign in to download</span>
        )}
      </div>
    </div>
  );
}

/** Publish, unpublish, and a way back to the wizard to add another file. */
function AuthorControls({ creation }: { creation: Creation }) {
  const setPublished = useSetPublished();
  const published = creation.status === "published";
  return (
    <div className="esm-actions">
      <ErrorAlert error={setPublished.error} title="That did not work" />
      <Link to="/upload" search={{ creation: creation.id }}>
        Add a file
      </Link>
      <Button
        variant={published ? "secondary" : "primary"}
        busy={setPublished.isPending}
        onClick={() => setPublished.mutate({ creationID: creation.id, publish: !published })}
      >
        {published ? "Unpublish" : "Publish"}
      </Button>
    </div>
  );
}
